import { HttpRequestState, httpRequestStates } from 'ngx-http-request-state';
import { Observable, of, OperatorFunction, retry, startWith, switchMap, timer } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

export const GENERIC_RETRY_STRATEGY_MAX_RETRY_ATTEMPTS = 3;
export const GENERIC_RETRY_STRATEGY_SCALING_DURATION = 1000;

let RETRY_STRATEGY_ENABLED = true;

export const enableRetryStrategy = (enabled = true) => {
  RETRY_STRATEGY_ENABLED = enabled;
};

/**
 * Generic retry strategy for http requests
 *
 * example:
 *
 * this._appService
 *   .getData(500)
 *   .pipe(
 *     retryWhen(genericRetryStrategy({
 *       scalingDuration: 2000,
 *     })),
 *     catchError(error => of(error))
 *   )
 *   .subscribe(e => console.log('Excluded code:', e.status));
 *
 * @param maxRetryAttempts
 * @param scalingDuration
 * @param log
 */
export const genericRetryStrategy = <T>({
  maxRetryAttempts = GENERIC_RETRY_STRATEGY_MAX_RETRY_ATTEMPTS,
  scalingDuration = GENERIC_RETRY_STRATEGY_SCALING_DURATION,
  isExcluded = () => false,
  log = (msg: string) => console.log(msg),
}: {
  maxRetryAttempts?: number;
  scalingDuration?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isExcluded?: (err: any, retryAttempt: number) => boolean;
  log?: (msg: string) => void;
} = {}): OperatorFunction<T, T> =>
  RETRY_STRATEGY_ENABLED
    ? retry({
        count: maxRetryAttempts,
        delay: (error, retryCount) => {
          const retryAttempt = retryCount + 1;
          // if maximum number of retries have been met
          // or response is a status code we don't wish to retry, throw error
          if (retryAttempt > maxRetryAttempts || isExcluded(error, retryAttempt)) {
            error.retryCount = retryCount;
            throw error;
          }
          log(`[genericRetryStrategy] Attempt ${retryAttempt}: retrying in ${retryAttempt * scalingDuration}ms`);
          // retry after 1s, 2s, etc...
          return timer(retryAttempt * scalingDuration);
        },
      })
    : (source: Observable<T>) => source;

export const limitEventTimeWithInterval = (intervalMs: number): OperatorFunction<unknown, boolean> => {
  return (source: Observable<unknown>) => {
    return source.pipe(
      map(() => false),
      startWith(true),
      switchMap((x) =>
        x
          ? of(true)
          : timer(intervalMs).pipe(
              map(() => true),
              startWith(false),
            ),
      ),
      distinctUntilChanged(),
    );
  };
};

/**
 * Returns true when an HTTP error response carries the backend's non-recoverable
 * signal (`error.recoverable === false`).
 *
 * Why: backend marks errors that are pointless to retry or recover from; the FE
 * uses this to short-circuit retry strategies and show a final/failure UI rather
 * than a transient toast.
 */
export const isFatalHttpError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const inner = (err as { error?: unknown }).error;
  if (!inner || typeof inner !== 'object') return false;
  return (inner as { recoverable?: unknown }).recoverable === false;
};

export const genericHttpStrategy =
  <T>(
    settings: {
      onError?: (error: unknown) => void;
      onValue?: (value: T) => void;
    } = {},
  ): OperatorFunction<T, HttpRequestState<T>> =>
  (source: Observable<T>) =>
    source.pipe(
      genericRetryStrategy<T>(),
      httpRequestStates() as OperatorFunction<T, HttpRequestState<T>>,
      tap({
        next: (res) => {
          if (res.error) {
            settings.onError?.(res.error);
          }
          if (res.value) {
            settings.onValue?.(res.value as T);
          }
        },
        error: (error) => {
          settings.onError?.(error);
        },
      }),
      // this tap is needed to catch errors from the retry strategy
      // mainly when error happens in passed callback functions
      // in other words, this handles errors in onValue callback
      tap({
        error: (error) => {
          settings.onError?.(error);
        },
      }),
    );
